import React, {Fragment, useCallback, useContext, useMemo} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {useParams} from "react-router-dom";

// Contexts
import {NavContext} from "../../contexts/nav.js";

// Components
import Modal from "../../components/Modal.js";

// Style
import {flex} from "../../style/components/mixins.js";
import {pad} from "../../style/components/variables.js";
import {voice} from "../../style/components/typography.js";
import {
  HeadingCenter,
  Form,
  Text,
  Button,
  FormGroup,
  List,
  ListItem,
  Warning,
  Inline,
  Span
} from "../../style/components/general.js";

// Utils
import {FIELD, GROUP} from "../../utils/builder.js";
import {getAncestryName} from "../checksheet-builder/helpers.js";

const ModalDeleteElement = ({
  visible,
  setVisible,
  targetElement,
  persistDelete,
  checksheets,
  saveWarningAction,
  builder
}) => {
  const {slug} = useParams();
  const {intermediateAction} = useContext(NavContext);

  const handleSubmit = e => {
    e.preventDefault();
    persistDelete();
  };

  const getComponentLink = useCallback(
    checksheetId => `/facilities/${slug}/checksheets/${checksheetId}/edit#${targetElement.name}`,
    [slug, targetElement]
  );

  const checkhseetsUsingTarget = useMemo(
    () =>
      checksheets?.filter(
        ({builder: checksheetBuilder}) => targetElement.name in checksheetBuilder.byId
      ),
    [checksheets, targetElement]
  );

  const checkhseetsUsingTargetNoSiblings = useMemo(
    () =>
      checksheets?.filter(({builder: checksheetBuilder}) => {
        if (!(targetElement.name in checksheetBuilder.byId)) return false;

        const {parentName} = checksheetBuilder.byId[targetElement.name];
        const parentElement = parentName ? checksheetBuilder.byId[parentName] : undefined;
        const siblingList = parentElement?.children || checksheetBuilder.allIds;

        return !siblingList?.filter(
          sibling =>
            sibling !== targetElement.name &&
            checksheetBuilder.byId[sibling] &&
            !checksheetBuilder.byId[sibling].deleted
        )?.length;
      }),
    [checksheets, targetElement]
  );

  const dependants = useMemo(() => {
    const matches = Object.values(builder.byId).filter(({formula, condition, deleted}) => {
      if (deleted) return false;
      let itemMatches = [];
      if (formula)
        itemMatches = [
          ...itemMatches,
          ...formula.filter(item => item.type === "variable" && item.value === targetElement.name)
        ];

      if (condition?.list)
        itemMatches = [
          ...itemMatches,
          ...condition.list.filter(item => item.depends === targetElement.name)
        ];

      return itemMatches.length > 0;
    });

    return matches;
  }, [builder, targetElement]);

  const childGenerates = useMemo(() => {
    const {children, element, name} = targetElement;
    if (!children || element === FIELD) return false;

    const matches = Object.values(builder.byId).filter(
      ({formula, condition, parentName: elementParent, deleted}) => {
        if (deleted) return false;
        let withinParent = false;

        if (name === elementParent) withinParent = true;
        else {
          const parent = builder.byId[elementParent];
          if (parent?.parentName === name) withinParent = true;
        }

        if (withinParent) return false;

        let itemMatches = [];
        if (formula)
          itemMatches = [
            ...itemMatches,
            ...formula.filter(
              item => item.type === "variable" && children.some(val => val === item.name)
            )
          ];

        if (condition?.list)
          itemMatches = [
            ...itemMatches,
            ...condition.list.filter(item => children.some(val => val === item.depends))
          ];

        return itemMatches.length > 0;
      }
    );

    return matches;
  }, [builder, targetElement]);

  const message = useMemo(() => {
    if (dependants?.length > 0) {
      return (
        <Text>
          Unable to delete! This field is being used in a formula or conditional:&nbsp;
          <Span>
            {dependants.map(({name: depName}) => (
              <Fragment key={depName}>
                {getAncestryName(builder.byId[depName], builder)}
                <br />
              </Fragment>
            ))}
          </Span>
        </Text>
      );
    }

    if (childGenerates?.length > 0) {
      return (
        <Text>
          Unable to delete! This {targetElement.element} contains fields that are being used in a
          formula or conditional:&nbsp;
          <Span>
            {childGenerates.map(({name: depName}) => (
              <Fragment key={depName}>
                {getAncestryName(builder.byId[depName], builder)}
                <br />
              </Fragment>
            ))}
          </Span>
        </Text>
      );
    }
    if (checkhseetsUsingTargetNoSiblings?.length) {
      const plural = checkhseetsUsingTargetNoSiblings.length > 1 ? "s" : "";
      return (
        <>
          <WarningWrapper>
            <WarningHeader>Warning!</WarningHeader>&nbsp;{targetElement.label} cannot be deleted as
            it is the only element within{" "}
            {targetElement.element === GROUP ? "" : "its containing group on "}
            {!plural && "an "}affected checksheet
            {plural}.
          </WarningWrapper>
          <br />
          <Text>
            To continue, please either delete the {targetElement.element} from the following
            checksheet{plural} or provide additional element(s) to{" "}
            {targetElement.element === GROUP
              ? "each checksheet"
              : "the containing group on each checksheet"}{" "}
            listed before deletion.
          </Text>
          <br />
          <Text bold>Affected Checksheet{plural}:</Text>
          <br />
          <List column>
            {checkhseetsUsingTargetNoSiblings.map(({id, name, frequency}) => (
              <ListItem key={`checksheet-${id}`}>
                <ChecksheetLink
                  href={intermediateAction ? undefined : getComponentLink(id)}
                  onClick={() => {
                    if (intermediateAction) intermediateAction(getComponentLink(id));
                  }}>
                  {name}
                  {frequency?.name ? ` (${frequency.name.toUpperCase()})` : ""}
                </ChecksheetLink>
              </ListItem>
            ))}
          </List>
          <br />
        </>
      );
    }

    if (checkhseetsUsingTarget?.length)
      return (
        <>
          <Text>
            This {targetElement.element} is currently being used by the following checksheets and
            will be removed:
          </Text>
          <br />
          <List column>
            {checkhseetsUsingTarget.map(({id, name, frequency}) => (
              <ListItem key={`checksheet-${id}`}>
                <ChecksheetLink
                  href={intermediateAction ? undefined : getComponentLink(id)}
                  onClick={() => {
                    if (intermediateAction) intermediateAction(getComponentLink(id));
                  }}>
                  {name}
                  {frequency?.name ? ` (${frequency.name.toUpperCase()})` : ""}
                </ChecksheetLink>
              </ListItem>
            ))}
          </List>
          <br />
          <Text>
            Are you sure you want to delete this {targetElement.element}? This action cannot be
            undone.
          </Text>
        </>
      );

    return (
      <Text>
        Are you sure you want to delete this {targetElement.element}? This action cannot be undone.
      </Text>
    );
  }, [
    dependants,
    childGenerates,
    checkhseetsUsingTargetNoSiblings,
    checkhseetsUsingTarget,
    targetElement.element,
    targetElement.label,
    builder,
    intermediateAction,
    getComponentLink
  ]);

  if (saveWarningAction)
    return (
      <Modal visible setVisible={setVisible} hasBackButton goBack={() => saveWarningAction(false)}>
        <HeadingCenter>Discard Changes?</HeadingCenter>
        <Text>
          Are you sure you want to leave this page? You have unsaved changes that will be lost.
        </Text>
        <br />
        <Inline>
          <WarningButton type="button" onClick={() => saveWarningAction(true)}>
            Continue and Discard
          </WarningButton>
          <Button type="button" onClick={() => saveWarningAction(false)}>
            Cancel
          </Button>
        </Inline>
      </Modal>
    );

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <ModalTitle>Delete Element</ModalTitle>

      <Form onSubmit={handleSubmit} noValidate>
        <FormGroup>{message}</FormGroup>
        {!checkhseetsUsingTargetNoSiblings?.length && (
          <Options>
            <Option type="button" onClick={() => setVisible(false)}>
              Cancel
            </Option>
            {!dependants?.length && !childGenerates?.length && (
              <Delete type="submit" data-testid="delete.confirm">
                Delete
              </Delete>
            )}
          </Options>
        )}
      </Form>
    </Modal>
  );
};

ModalDeleteElement.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  persistDelete: PropTypes.func.isRequired,
  targetElement: PropTypes.objectOf(PropTypes.any).isRequired,
  checksheets: PropTypes.arrayOf(PropTypes.any),
  saveWarningAction: PropTypes.func,
  builder: PropTypes.objectOf(PropTypes.any).isRequired
};

// Style Overrides
const ModalTitle = styled(HeadingCenter)`
  margin-bottom: ${pad * 2}px;
`;

const Options = styled.div`
  ${flex("row", "nowrap", "start", "start")};
  width: 100%;
`;

const Option = styled(Button)`
  margin: 0 ${pad / 2}px;
`;

const Delete = styled(Option)`
  background: ${props => props.theme.error};
  color: ${props => props.theme.tertiary};

  &:disabled {
    opacity: 0.6;
  }
`;

const WarningWrapper = styled(Warning)`
  margin-bottom: ${pad / 2}px;
  color: ${({theme}) => theme.secondary};

  ${voice.normal}
`;

const WarningHeader = styled.strong`
  color: ${({theme}) => theme.warning};
`;

const WarningButton = styled(Button)`
  color: ${({theme}) => theme.error};
`;

const ChecksheetLink = styled.a`
  border: 0;
  background: 0;
  text-decoration: underline;

  svg {
    fill: ${props => props.theme.secondary};
  }
`;

export default ModalDeleteElement;
